import { Size, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import styles from './DuplicateModal.module.css';

/**
 * The duplicate modal (piece 10, decision 0042). 🔨 It shows both records and
 * lets a person decide; the system never auto-merges and never silently blocks.
 * It reports why it matched — *"same email domain, similar name"* — never a
 * generic *"possible duplicate"*.
 *
 * 🔨 On a Person the other-person answer is effortless and the same-person
 * answer considered, never the reverse and never a default: the effortless one
 * is the Footer's Keep both, and the considered one is a control beside the
 * candidate it names. A duplicate is a tidy-up and a conflation is a forensic
 * exercise, which is what the asymmetry is for.
 */
export interface DuplicateHit {
  id: string;
  label: string;
  tier: string;
  reason: string;
  href: string;
  /** A Person candidate's own name parts, so a screen that picks the candidate never splits the label to find them. */
  firstName?: string;
  lastName?: string;
  /** A Person candidate's context, because "there is a person with the same name" is not enough to answer with. */
  companies?: string[];
  emails?: string[];
  lastActiveAt?: string | null;
}

export interface DuplicateModalProps {
  open: boolean;
  /** What the person is creating, drawn beside each candidate. */
  subject: string;
  /** A subject detail worth comparing — a typed address, a work email. */
  subjectDetail?: string | null;
  hits: readonly DuplicateHit[];
  /** A Person asks *is this the same human*; every other record asks *is this the same record*. */
  kind: 'person' | 'record';
  /** The effortless answer: the row stands and both records exist. */
  onKeepBoth: () => void;
  /** The considered answer, named per candidate: open the existing one, or on a Person link to them instead. */
  onUseExisting: (hit: DuplicateHit) => void;
  onCancel: () => void;
  openerId?: string | null;
}

export function DuplicateModal({ open, subject, subjectDetail = null, hits, kind, onKeepBoth, onUseExisting, onCancel, openerId = null }: DuplicateModalProps) {
  const useLabel = kind === 'person' ? t('duplicate.thisPerson') : t('duplicate.openExisting');
  return (
    <Modal
      open={open}
      title={kind === 'person' ? t('duplicate.personTitle') : t('duplicate.title')}
      subtitle={kind === 'person' ? t('duplicate.personSubtitle') : t('duplicate.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="duplicate"
      width="wide"
      actions={[
        // 🔨 The effortless answer sits in the Footer's Primary and the
        // considered one beside the candidate; neither is preselected.
        { kind: 'primary', label: kind === 'person' ? t('duplicate.somebodyElse') : t('duplicate.keepBoth'), onAct: onKeepBoth },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-duplicate-kind={kind}>
        <p className={styles.lead}>{kind === 'person' ? t('duplicate.personLead') : t('duplicate.lead')}</p>
        {hits.map((hit) => (
          <div key={hit.id} className={styles.pair} data-duplicate-hit={hit.id}>
            <div className={styles.side}>
              <span className={styles.heading}>{t('duplicate.thisOne')}</span>
              <span className={styles.name}>{subject}</span>
              {subjectDetail ? <span className={styles.detail}>{subjectDetail}</span> : null}
            </div>
            <div className={styles.side}>
              <span className={styles.heading}>{t('duplicate.alreadyHere')}</span>
              <span className={styles.name}>{hit.label}</span>
              {/* Which Companies they are linked to, the email on those links, and when they were last active (decision 0042). */}
              {hit.companies && hit.companies.length > 0 ? <span className={styles.detail}>{[t('duplicate.at'), hit.companies.join(', ')].join(' ')}</span> : null}
              {hit.emails && hit.emails.length > 0 ? <span className={styles.detail}>{hit.emails.join(', ')}</span> : null}
              {hit.lastActiveAt ? <span className={styles.detail}>{[t('duplicate.lastActive'), hit.lastActiveAt].join(' ')}</span> : null}
              <span className={styles.reason} data-duplicate-reason={hit.tier}>{hit.reason}</span>
              <span className={styles.considered}>
                <Button size={Size.Small} text={[useLabel]} actions={[() => onUseExisting(hit)]} />
              </span>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
